/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { PrimaryRouteRenderContext } from "abreact/incubator/ui/app/router/routeRenderContext/PrimaryRouteRenderContext";
import { AbErrorBoundary } from "abreact/incubator/ui/component/AbErrorBoundary";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbBusyIndicator } from "abreact/progress/AbBusyIndicator";
import * as AbHooks from "abreact/utils/AbHooks";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { observer } from "mobx-react";
import * as React from "react";

// Do NOT create react context in global scope or get cyclic dependency errors (ABSDK-5281)
// Instead, lazily create on very first reactRouteRenderContext() call (which is out of global scope!)
let _reactRouteRenderContext = undefined;

/**
 * Provide a react context for RouteRenderContext that will default to the
 * PrimaryRouteRenderContext singleton (aka mainArea RouteRenderContext)
 * if the caller is not in scope of a RouteRenderContext (e.g., in the banner area).
 *
 * You may NOT call this from static scope (see AbErrorBoundary for how to use
 * 'static contextType' but still get defaulting to the PrimaryRouteRenderContext.SINGLETON)
 */
export function reactRouteRenderContext() {
  if (!_reactRouteRenderContext) {
    _reactRouteRenderContext = /*#__PURE__*/React.createContext(PrimaryRouteRenderContext.SINGLETON);
  }
  return _reactRouteRenderContext;
  // Absolutely do NOT crate new createContext on very call, as new React.Context value on every call
  // cause all components to always unmount (via Memoize) as always new React.Context instance in scope!
  // return React.createContext<RouteRenderContext>(PrimaryRouteRenderContext.SINGLETON);
}

// react component classes can access current RouteRenderContext via this.context after adding the below to their component class:
//         static override contextType = ReactRouteRenderContext;    // causes this.context to be populated
//         declare context: RouteRenderContext;        // strongly typed this.context
//    OR via
//       <RouteRenderContextContext.Consumer>{value => /* render something based on the context value */}</RouteRenderContextContext.Consumer
//    The consumer approach is useful when need access to multiple react contexts within a react component as can be be nested

const LOG = LogUtils.getLogger(__filename);

/**
 * Automatically re-renders the current route of the RouteContext on major route changes.
 * If the current routeHandler is not set, it displays the BusyIndicator.  It also provides the
 * React context that stores the current RouteRenderContext, which is used by AbRouteLink (and any other react component)
 * that needs to determine the active RouteRenderContext.  By default, the rendered route is wrapped
 * in an AbErrorBoundary, unless the suppressErrorBoundary=true prop is provided
 */
export const AbRouteRender = observer(function (props) {
  const {
    routeRenderContext,
    renderWrapperFn,
    busyIndicatorProps,
    renderUnmountedFn
  } = props;
  AbHooks.useOnMount(() => {
    LOG.debug("Mounting <AbRouteRender />");
    return () => LOG.debug("Unmounting <AbRouteRender />");
  });

  // Rendering hierarchy: ReactRouteRenderContext?, AbErrorBoundary?, div, renderWrapperFn?, renderedRoute|busyIndicator
  // Bottom from innermost to outermost (reverse order)

  // Note react component class render() returns more general ReactNode, while react component fn returns ReactElement
  // Have to support both, so keep in more general ReactNode until calculate the retRender step below,
  // which wraps a potential ReactNode in a fragment to convert it to a ReactElement.
  const currRH = routeRenderContext?.currentRouteHandler ?? null;
  LOG.info(currRH ? "Rendering currRH.renderView()" : "Rendering unmounted route as currentRouteHandler is undefined");
  const renderedRoute = currRH ? currRH.renderView() : renderUnmountedFn ? renderUnmountedFn() : /*#__PURE__*/React.createElement(AbBusyIndicator, busyIndicatorProps);

  // renderWrapperFn can optionally detect currRH to determine if want to wrap the unmount case (e.g., busy indicator)
  let wrappedRenderedRoute = renderWrapperFn ? renderWrapperFn(renderedRoute, currRH) : renderedRoute;

  // ab-route-render css class has height: 100% so that div behaves well in flex layouts
  const divProps = ReactUtils.divProps("ab-route-render", props);
  wrappedRenderedRoute = /*#__PURE__*/React.createElement("div", divProps, wrappedRenderedRoute);
  if (!props.suppressErrorBoundary) {
    // Secondary route contexts are not directly tied to url changes,
    // so do not use AbErrorBoundary prop tryAgainOnRouteChange.
    wrappedRenderedRoute = /*#__PURE__*/React.createElement(AbErrorBoundary, {
      showRefreshButton: true
    }, wrappedRenderedRoute);
  }
  if (routeRenderContext) {
    // Portal starts rendering before services (including router) are
    const ReactRouteRenderContext = reactRouteRenderContext();
    wrappedRenderedRoute = /*#__PURE__*/React.createElement(ReactRouteRenderContext.Provider, {
      value: routeRenderContext
    }, wrappedRenderedRoute);
  }

  // convert possible ReactNode to ReactElement via fragment wrap
  return /*#__PURE__*/React.createElement(React.Fragment, null, wrappedRenderedRoute);
});
// Set component display name, as the observer wrapper obscures it.
// If you don't do this, this component will have the name "_c2" in the chrome debugger component view
AbRouteRender.displayName = "AbRouteRender";
//# sourceMappingURL=AbRouteRender.js.map
